import services from "../services";
import Utils from "../Utils";
import router from "./../router";

const strict = true;

const getDefaultState = () => {
  return {
    groupList: [],
    activeGroup: null,
  };
};

const state = getDefaultState();

const getters = {
  getGroupList: state => state.groupList,
  getActiveGroup: state => state.activeGroup,
};

const mutations = {
  RESET(state) {
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_GROUP_LIST(state, groupList) {
    state.groupList = groupList;
  },
  SET_ACTIVE_GROUP(state, group) {
    state.activeGroup = group;
  },
};

const actions = {
  retrieveGroups({ commit, rootGetters }) {
    const clientList = rootGetters["clients/getClientList"];
    return Promise.allSettled(
      clientList.map(client =>
        services.GroupService.retrieveGroupByClient(client._id)
      )
    ).then(results => {
      const groups = [];
      const seen = new Set();
      results.forEach(result => {
        if (result.status === "fulfilled" && result.value) {
          const group = result.value;
          if (!seen.has(group._id)) {
            seen.add(group._id);
            groups.push(group);
          }
        }
      });
      commit("SET_GROUP_LIST", groups);
      return groups;
    });
  },

  retrieveGroup({ commit }, groupId) {
    return services.GroupService.retrieveGroup(groupId)
      .then(response => {
        commit("SET_ACTIVE_GROUP", response);
        return response;
      })
      .catch(error => {
        commit("SET_ACTIVE_GROUP", null);
        checkRedirectionByStatus(error.status, commit);
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(new Error());
      });
  },

  createGroup({ commit }, payload) {
    return services.GroupService.createGroup(payload)
      .then(response => {
        commit("SET_ACTIVE_GROUP", response);
        return response;
      })
      .catch(error => {
        checkRedirectionByStatus(error.status, commit);
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(new Error());
      });
  },

  updateGroup({ commit }, { groupId, payload }) {
    return services.GroupService.updateGroup(groupId, payload)
      .then(response => {
        commit("SET_ACTIVE_GROUP", response);
        return response;
      })
      .catch(error => {
        checkRedirectionByStatus(error.status, commit);
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(new Error());
      });
  },

  deleteGroup({ commit }, groupId) {
    return services.GroupService.deleteGroup(groupId)
      .then(() => {
        commit("SET_ACTIVE_GROUP", null);
      })
      .catch(error => {
        checkRedirectionByStatus(error.status, commit);
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(new Error());
      });
  },

  addMemberToGroup({ commit, getters }, { groupId, payload }) {
    return services.GroupService.addMemberToGroup(groupId, payload)
      .then(() => {
        return services.GroupService.retrieveGroup(groupId);
      })
      .then(response => {
        commit("SET_ACTIVE_GROUP", response);
        return response;
      })
      .catch(error => {
        checkRedirectionByStatus(error.status, commit);
        // The API answers add-member refusals with a stable errorCode
        // (e.g. already_in_another_group). Those are expected outcomes, not
        // system failures: show them inline instead of the generic dialog.
        const messageKey = addMemberErrorKey(error);
        if (messageKey) {
          return Promise.reject(addMemberError(messageKey, error));
        }
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(addMemberError(null, error));
      });
  },

  removeMemberFromGroup({ commit }, { groupId, payload }) {
    return services.GroupService.removeMemberFromGroup(groupId, payload)
      .then(() => {
        return services.GroupService.retrieveGroup(groupId);
      })
      .then(response => {
        commit("SET_ACTIVE_GROUP", response);
        return response;
      })
      .catch(error => {
        checkRedirectionByStatus(error.status, commit);
        commit("error/SET_ERROR", { status: error.status }, { root: true });
        return Promise.reject(new Error());
      });
  },
};

// --- add-member error mapping ----------------------------------------------
// The API returns { errorCode, errorDetails } for every add-member refusal.
const ADD_MEMBER_MESSAGE_KEYS = {
  already_in_another_group: "page.groups.errAlreadyInOtherGroup",
  already_member_of_this_group: "page.groups.errAlreadyMember",
  invitation_already_pending: "page.groups.errInvitationPending",
  self_invite: "page.groups.errSelfInvite",
  domain_not_allowed: "page.groups.errDomainNotAllowed",
  license_limit_reached: "page.groups.errLicenseLimit",
  new_user_details_required: "page.groups.errNewUserDetails",
  new_user_terms_required: "page.groups.errNewUserDetails",
};

function apiErrorBody(error) {
  return (
    (error && (error.body || (error.response && error.response.body))) || {}
  );
}

function apiErrorMessage(error) {
  const body = apiErrorBody(error);
  return body.errorDetails || body.error || (error && error.message) || "";
}

// Rejections must be real Errors (eslint prefer-promise-reject-errors), so the
// two fields GroupMembers.vue reads travel as properties on the Error:
//   messageKey — i18n key for a known refusal, null for an unexpected failure
//   apiMessage — the API's own wording, used when there is no key
function addMemberError(messageKey, error) {
  const apiMessage = apiErrorMessage(error);
  const err = new Error(apiMessage || "Add member failed");
  err.messageKey = messageKey;
  err.apiMessage = apiMessage;
  return err;
}

function addMemberErrorKey(error) {
  const body = apiErrorBody(error);
  if (body.errorCode && ADD_MEMBER_MESSAGE_KEYS[body.errorCode]) {
    return ADD_MEMBER_MESSAGE_KEYS[body.errorCode];
  }
  // Fallback for an API not yet redeployed with errorCode.
  if (error && error.status === 409)
    return "page.groups.errAlreadyInOtherGroup";
  return null;
}

function checkRedirectionByStatus(status, commit) {
  switch (status) {
    case Utils.ERRORS.UNAUTHORIZED:
      commit("auth/SET_IS_LOGGED_IN", false, { root: true });
      router.replace({ name: "Login" }).catch(() => {});
      break;
    case Utils.ERRORS.NOT_FOUND:
    case Utils.ERRORS.METHOD_NOT_ALLOWED:
      router
        .replace({ name: "Groups", params: { showErrorDialog: true } })
        .catch(() => {});
      break;
    default:
      break;
  }
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions,
};
